import { usePath } from '@/stores/usePath'
import { useUnauthMessage } from '@/stores/useUnauthMessage'
import { useUser } from '@/stores/useUser'
import { createRouter, createWebHistory } from 'vue-router'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/layouts/MainLayout.vue'),
    },
    {
      // mobilappos webview-ban megjelenített felületek, emiatt ezeknél nincs szükség az oldalsó menüre stb
      path: '/mobil',
      component: () => import('@/layouts/MobilLayout.vue'),
      children: [
        {
          path: 'fajlok',
          name: 'fajlok_mobil',
          component: () => import('@/views/FajlokView.vue'),
          meta: { title: 'Dokumentumok', requiresAuth: true, roles: ['admin', 'ugyfel', 'konyvelo'] },
        },
      ]
    },
    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: 'mcp/letoltes/:keres',
          name: 'mcp_letoltes',
          component: () => import('@/views/McpLetoltesView.vue'),
          meta: { title: 'Dokumentum letöltése', requiresAuth: true, roles: ['admin', 'ugyfel', 'konyvelo'] },
        },
        {
          path: 'logs',
          name: 'logs',
          component: () => import('@/views/LogokView.vue'),
          meta: { title: 'Audit', requiresAuth: true, roles: ['admin'] },
        },
        // 05-be nem kell
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { title: 'Előfizetési adatok', requiresAuth: true, roles: ['admin'] },
        },
        {
          path: 'felhasznalok',
          name: 'felhasznalok',
          component: () => import('@/views/FelhasznalokView.vue'),
          meta: { title: 'Felhasználók', requiresAuth: true, roles: ['vxadmin','admin','ugyfel'] },
        },
        {
          path: 'ugyfelcegek',
          name: 'ugyfelcegek',
          component: () => import('@/views/UgyfelCegekView.vue'),
          meta: {title: 'Ügyfél cégek', requiresAuth: true, roles: ['vxadmin','admin']}
        },
        {
          path: 'fajlok',
          name: 'fajlok',
          component: () => import('@/views/FajlokView.vue'),
          meta: { title: 'Dokumentumok', requiresAuth: true, roles: ['admin', 'ugyfel', 'konyvelo'] },
        },
        {
          path: 'lomtar',
          name: 'lomtar',
          component: () => import('@/views/LomtarView.vue'),
          meta: { title: 'Lomtár', requiresAuth: true, roles: ['admin', 'ugyfel', 'konyvelo'] },
        },
        {
          path: 'kategoriak',
          name: 'kategoriak',
          component: () => import('@/views/KategoriaSzotarView.vue'),
          meta: { title: 'Dokumentum-kategóriák', requiresAuth: true, roles: ['vxadmin','admin'] },
        },
        {
          path: 'elofizetesek',
          name: 'elofizetesek',
          component: () => import('@/views/ElofizetesekView.vue'),
          meta: { title: 'Előfizetések', requiresAuth: true, roles: ['vxadmin'] }
        },
        {
          path: 'mobil-app',
          name: 'mobil-app',
          component: () => import('@/views/MobilAppView.vue'),
          meta: { title: 'Mobilalkalmazás', requiresAuth: true, roles: ['admin', 'konyvelo', 'ugyfel', 'vxadmin'] },
        },
        {
          path: 'pulse',
          name: 'pulse',
          component: () => import('@/views/PulseView.vue'),
          meta: { title: 'Pulse', requiresAuth: true, roles: ['vxadmin']}
        },
      ],
    },
    {
      path: '/auth',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        {
          path: 'ai-engedelyezes',
          name: 'ai_engedelyezes',
          component: () => import('@/views/AiEngedelyezesView.vue'),
          meta: { title: 'AI-hozzáférés engedélyezése', requiresAuth: true, roles: ['admin', 'konyvelo', 'ugyfel'] },
        },
        {
          path: 'bejelentkezes',
          name: 'bejelentkezes',
          component: () => import('@/views/LoginView.vue'),
          meta: { title: 'Bejelentkezés', requiresAuth: false, roles: [] },
        },
        {
          path: 'regisztracio',
          name: 'regisztracio',
          component: () => import('@/views/RegisztracioView.vue'),
          meta: { title: 'Regisztráció', requiresAuth: false, roles: [] },
        },
        {
          path: 'jelszobeallitas/:id',
          name: 'jelszobeallitas',
          component: () => import('@/views/JelszoBeallitasView.vue'),
          props: true,
          meta: { title: 'Jelszó beállítása', requiresAuth: false, roles: [] }
        },
        {
          path: 'hozzaferes_megtagadva',
          name: 'hozzaferes_megtagadva',
          component: () => import('@/views/UnauthorizedView.vue'),
          meta: { title: 'Nincs hozzáférés', requiresAuth: true, roles: [] } // <- fontos!
        }
      ],
    },
    {
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          component: () => import('@/views/NotFoundView.vue'),
          meta: { title: 'Az oldal nem található', requiresAuth: false, roles: [] },
        },
  ],
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUser()
  let user = null
  const path = usePath()
  const requiresAuth = !!to.meta.requiresAuth
  const allowedRoles: string[] = (to.meta.roles as string[]) || []
  const unauthmessage = useUnauthMessage()

  try {
    user = await userStore.fetchAndGetUser()
  } catch (error) {
    // elvileg csak akkor dob a fetchandgetuser hibát ha le van tiltva a user
    // ha simán nincs bejelentkezve, azt kezeli és null-ra állítja a usert
    unauthmessage.setMessage("A felhasználói fiók le van tiltva.")
    return next({ name: 'hozzaferes_megtagadva', query: { reason: '403' } })
  }
  if (to.name === 'home') {
    return next({
      name: user?.tipus === 'vxadmin' ? 'felhasznalok' : 'fajlok'
    })
  }

  // ha a előbbi ellenőrzésen átjutott, a biztonság kedvéért mégegyszer csekkoljuk a státuszát
  if (requiresAuth && user && to.name != 'hozzaferes_megtagadva'){
    const statusz = (user.tipus == 'admin' || user.tipus == 'vxadmin') || (user.ugyfel?.aktiv ?? user.konyvelo?.aktiv)
    if (!statusz){
      unauthmessage.setMessage("A felhasználói fiók le van tiltva.")
      return next({ name: 'hozzaferes_megtagadva', query: { reason: '403' } })
    }
  }

  // Bejelentkezett user ne menjen a loginra, regisztrációra
  if (user && ['bejelentkezes', 'regisztracio'].includes(String(to.name))) {
    return next({ name: 'home' })
  }

  // Auth szükséges, de nincs user → bejelentkezés page
  if (requiresAuth && !user) {
    path.setPath(to.fullPath)
    return next({ name: 'bejelentkezes' })
  }

  // Szerepkör ellenőrzés
  if (requiresAuth && allowedRoles.length > 0) {
    const role = user?.tipus
    if (!role || !allowedRoles.includes(role)) {
      unauthmessage.setMessage("Nincs jogosultság a kért oldal megtekintéséhez.")
      return next({ name: 'hozzaferes_megtagadva', query: { reason: '403' } })
    }
  }

  next()
})

export default router
